'use client'

import type { ComponentProps } from 'react'

import {
  ConversationMessageType,
  ConversationMessage as ConversationMessageProps,
  StatusMessage as StatusMessageType,
  Message as MessageType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  OfferMessage as OfferMessageType,
  OfferRequestMessage as OfferRequestMessageType,
} from '@marketplace-web/offers/price-offers-data'
import { OfferMessage } from '@marketplace-web/offers/price-offers-feature'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { FeedbackRequestMessage } from '@marketplace-web/help-center-conversation-support/automated-support-feedback-feature'

import StatusMessage from '../StatusMessage'
import ReportSuggestion from '../ReportSuggestion'
import UserMessage from '../UserMessage'
import OfferRequestMessage from '../OfferRequestMessage'

type Props = ConversationMessageProps & {
  transactionId?: number
  isGrouped: boolean
  isLast: boolean
  isCurrentAuthor: boolean
  conversationId: string | number | undefined
  toggleSidebar?: () => void
  openOfferModal?: () => void
} & Pick<ComponentProps<typeof UserMessage>, 'onThumbnailClick'>

const ConversationMessage = ({ conversationId, ...props }: Props) => {
  const { entityType, message } = props
  const userSide = useConversationContext().conversation?.transaction?.userSide ?? undefined
  const isInlineCsatEnabled = useFeatureSwitch('launch_web_inline_csat')

  switch (entityType) {
    case ConversationMessageType.ActionMessage:
    case ConversationMessageType.StatusMessage:
      return <StatusMessage {...props} message={message as StatusMessageType} />
    case ConversationMessageType.ReportSuggestion:
      return <ReportSuggestion conversationId={conversationId} userSide={userSide} />
    case ConversationMessageType.OfferMessage:
      return <OfferMessage {...props} message={message as OfferMessageType} />
    case ConversationMessageType.OfferRequestMessage:
      return <OfferRequestMessage {...props} message={message as OfferRequestMessageType} />
    case ConversationMessageType.Message:
    case ConversationMessageType.PortalMessage:
      return <UserMessage {...props} message={message as MessageType} />
    case ConversationMessageType.AutoReplyFeedbackRequestMessage:
    case ConversationMessageType.ChatbotFeedbackRequestMessage:
    case ConversationMessageType.ConversationalChatbotFeedbackRequestMessage:
      return isInlineCsatEnabled ? <FeedbackRequestMessage {...props} /> : null
    default:
      return null
  }
}

export default ConversationMessage
